import { useState, useRef, useCallback, useEffect } from 'react';

export type AudioRecorderStatus = 'idle' | 'recording' | 'processing' | 'error' | 'unsupported';

export interface UseAudioRecorderOptions {
  /** Called with the recorded audio as a base64 string and its MIME type when recording stops. */
  onAudioFile: (base64: string, mimeType: string) => void;
  /** Called when an error occurs. */
  onError?: (message: string) => void;
}

export interface UseAudioRecorderResult {
  status: AudioRecorderStatus;
  isSupported: boolean;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

function getPreferredMimeType(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

export function useAudioRecorder({
  onAudioFile,
  onError,
}: UseAudioRecorderOptions): UseAudioRecorderResult {
  const isSupported =
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia;

  const [status, setStatus] = useState<AudioRecorderStatus>(
    isSupported ? 'idle' : 'unsupported'
  );

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Keep latest callbacks stable without causing re-renders
  const onAudioFileRef = useRef(onAudioFile);
  const onErrorRef = useRef(onError);
  useEffect(() => { onAudioFileRef.current = onAudioFile; }, [onAudioFile]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const stop = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const start = useCallback(async () => {
    if (!isSupported) {
      onErrorRef.current?.('Audio recording is not supported in this browser.');
      setStatus('unsupported');
      return;
    }

    // Stop any ongoing recording first
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getPreferredMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstart = () => {
        setStatus('recording');
      };

      recorder.onstop = () => {
        setStatus('processing');

        // Stop all tracks so the browser mic indicator turns off
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;

        const blob = new Blob(chunksRef.current, {
          type: mimeType || 'audio/webm',
        });
        chunksRef.current = [];

        if (blob.size === 0) {
          setStatus('idle');
          onErrorRef.current?.('No audio was recorded.');
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          // Strip the data URL prefix to get pure base64
          const base64 = dataUrl.split(',')[1];
          setStatus('idle');
          onAudioFileRef.current(base64, blob.type || mimeType || 'audio/webm');
        };
        reader.onerror = () => {
          setStatus('error');
          onErrorRef.current?.('Failed to process audio recording.');
        };
        reader.readAsDataURL(blob);
      };

      recorder.onerror = () => {
        setStatus('error');
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        onErrorRef.current?.('An error occurred during audio recording.');
      };

      recorder.start();
    } catch (err: any) {
      setStatus('error');
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        onErrorRef.current?.('Microphone access denied. Please allow microphone access in your browser settings.');
      } else {
        onErrorRef.current?.('Failed to start audio recording.');
      }
    }
  }, [isSupported]);

  const toggle = useCallback(() => {
    if (status === 'recording') {
      stop();
    } else {
      start();
    }
  }, [status, start, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  return { status, isSupported, start, stop, toggle };
}
